import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { DecimalConfigService } from 'app/services/decimal-config.service';
import { CompanyCurrencyPipe } from 'app/pipes/currency.pipe';

/**
 * Currency Cell Renderer Component
 *
 * Renders currency values with decimal formatting and currency symbol.
 * Uses CompanyCurrencyPipe to get the currency symbol from the active company.
 *
 * Features:
 * - Displays currency symbol before the value (e.g., "$ 150.00", "L 2,500.00")
 * - Uses DecimalConfigService for configurable decimal places
 * - Uses Angular's DecimalPipe for thousands separators
 * - Falls back to "-" for null/undefined values
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'unitPrice',
 *   headerName: 'Precio',
 *   cellRenderer: CurrencyCellRendererComponent,
 *   sortable: true,
 *   cellClass: 'text-right'
 * }
 * ```
 *
 * Output examples:
 * - USD company: "$ 150.00"
 * - HNL company: "L 2,500.00"
 * - EUR company: "€ 75.00"
 */
@Component({
  selector: 'app-currency-cell-renderer',
  standalone: true,
  imports: [CommonModule, DecimalPipe, CompanyCurrencyPipe],
  template: `
    <div class="flex items-center justify-end gap-1 pr-14">
      <span class="currency-symbol font-medium opacity-80">{{ currencySymbol }}</span>
      <span class="amount">{{ formattedValue }}</span>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyCellRendererComponent implements CellRenderer<any, number> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, number>;

  private readonly configService = inject(DecimalConfigService);
  private readonly decimalPipe = new DecimalPipe('en-US');
  private readonly currencyPipe = inject(CompanyCurrencyPipe);

  /**
   * Get formatted number value (with thousands separators and decimals)
   */
  get formattedValue(): string {
   
    const value = this.params?.value ?? this.params?.data?.unitPrice;

    // Handle null/undefined values
    if (value === null || value === undefined) {
      return '-';
    }

    // Get decimal places from configuration
    const decimals = this.configService.getDecimals();
    return this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? '-';
  }

  /**
   * Get currency symbol from CompanyCurrencyPipe
   * Returns symbol like "$", "L", "€" based on active company's currency
   */
  get currencySymbol(): string {
    return this.currencyPipe.transform('symbol'); // Get symbol, not code
  }
}
